/**
 * services/rulesService.js - "Remasterizado" para Multi-Inquilino
 *
 * Este servicio maneja la comunicación con la API del backend para
 * todas las operaciones relacionadas con las reglas del usuario autenticado.
 */
import { authFetchJson } from './authFetch';
import { API_URL } from '../config/api';

/**
 * Función auxiliar para obtener las cabeceras de autenticación.
 * @returns {Promise<HeadersInit>}
 */


/**
 * 🔹 Envía el nuevo orden de las reglas al backend de forma segura.
 * @param {string[]} orderedRuleIds - Un array con los IDs de las reglas en su nuevo orden.
 * @returns {Promise<object>} La respuesta de confirmación del servidor.
 */
export async function reorderRules(orderedRuleIds) {
    return await authFetchJson(
        `${API_URL}/rules/reorder`,
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ orderedRuleIds }),
        },
        { retry: 1 }
    );
}

/**
 * 🔹 Obtener todas las reglas del usuario autenticado
 * @returns {Promise<object>} La lista de reglas y el resumen de uso
 */
export async function getRules() {
    return await authFetchJson(
        `${API_URL}/rules`,
        {},
        { retry: 1 }
    );
}

/**
 * 🔹 Crear una nueva regla
 * @param {object} ruleData - Los datos de la nueva regla
 * @returns {Promise<object>} La regla creada con su ID
 */
export async function createRule(ruleData) {
    return await authFetchJson(
        `${API_URL}/rules`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(ruleData),
        },
        { retry: 0 } // No reintentar en POST para evitar duplicados accidentales
    );
}

// ✅ Creación en "batch": el backend NO tiene endpoint /rules/batch, así que
// creamos las reglas una por una con createRule (POST /rules), resolviendo las
// referencias parentId que vienen como id-slug de la plantilla -> ids reales.
export async function createRulesBatch(rulesArray) {
    if (!Array.isArray(rulesArray) || rulesArray.length === 0) return [];

    // Ordenar padres antes que hijos (por profundidad en la cadena parentId)
    const byId = {};
    rulesArray.forEach((r) => { if (r && r.id) byId[r.id] = r; });
    const depthOf = (r, seen = new Set()) => {
        if (!r || !r.parentId || !byId[r.parentId] || seen.has(r.id)) return 0;
        seen.add(r.id);
        return 1 + depthOf(byId[r.parentId], seen);
    };
    const ordered = [...rulesArray].sort((a, b) => depthOf(a) - depthOf(b));

    const slugToRealId = {};
    const created = [];
    for (const rule of ordered) {
        const { id: slugId, parentId: slugParentId, ...rest } = rule;
        const realParentId = slugParentId ? (slugToRealId[slugParentId] || null) : null;
        const result = await createRule({ ...rest, parentId: realParentId });
        if (slugId && result && result.id) slugToRealId[slugId] = result.id;
        created.push(result);
    }
    return created;
}

/**
 * 🔹 Actualizar una regla existente
 * @param {string} ruleId - El ID de la regla a actualizar
 * @param {object} ruleData - Los datos a actualizar
 * @returns {Promise<object>} La regla actualizada
 */
export async function updateRule(ruleId, ruleData) {
    return await authFetchJson(
        `${API_URL}/rules/${ruleId}`,
        {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(ruleData),
        },
        { retry: 1 }
    );
}

/**
 * 🔹 Eliminar una regla existente
 * @param {string} ruleId - El ID de la regla a eliminar
 * @returns {Promise<void>} 
 */
export async function deleteRule(ruleId) {
    return await authFetchJson(
        `${API_URL}/rules/${ruleId}`,
        {
            method: "DELETE",
        },
        { retry: 1 }
    );
}

/**
 * 🔹 Forzar recarga del caché de reglas en el servidor
 * @returns {Promise<object>} Resultado de la invalidación
 */
export async function forceReloadCache() {
    return await authFetchJson(
        `${API_URL}/rules/force-reload-cache`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" }
        },
        { retry: 1 }
    );
}

/**
 * 🔹 Copiar nodo con o sin descendencia
 * @param {string} ruleId - El ID de la regla original
 * @param {string|null} newParentId - El ID del nuevo padre o null si es raíz
 * @param {string} copyMode - 'with-children' o 'single'
 * @returns {Promise<object>} Resultado de la copia
 */
export async function copyRule(ruleId, newParentId = null, copyMode = 'with-children') {
    return await authFetchJson(
        `${API_URL}/rules/${ruleId}/copy-with-children`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ newParentId, copyMode }),
        },
        { retry: 0 }
    );
}

/**
 * 🔹 Log action to server
 * @param {object} logData - Datos para el log
 * @returns {Promise<object>} Resultado del log
 */
export async function logRuleAction(logData) {
    return await authFetchJson(
        `${API_URL}/rules/log`,
        {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(logData),
        },
        { retry: 1 }
    );
}
